import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    SOURCE_REPORT_LANGUAGE,
    getReportDirection,
    translateReportHtml,
    translateWorkbook,
} from '../Utils/ReportTranslation';

/**
 * Per-report language state for the HTML ledger reports. The report language starts in Hebrew
 * (the language reports are generated in) and never follows the application UI language.
 *
 * - `displayHtml`: the preview HTML in the selected language.
 * - `translateHtml(html)` / `translateWb(wb)`: translate export content (PDF, print, chat, Excel)
 *   just before it is sent; failures are toasted and rethrown so the export is aborted.
 */
export const useReportTranslation = (reportHtml, token) => {
    const { t } = useTranslation();
    const [reportLanguage, setReportLanguage] = useState(SOURCE_REPORT_LANGUAGE);
    const [translatedHtml, setTranslatedHtml] = useState('');
    const [isTranslating, setIsTranslating] = useState(false);

    const isSourceLanguage = reportLanguage === SOURCE_REPORT_LANGUAGE;

    useEffect(() => {
        if (isSourceLanguage || !reportHtml) {
            setIsTranslating(false);
            return undefined;
        }

        let cancelled = false;
        setIsTranslating(true);
        translateReportHtml(reportHtml, reportLanguage, token)
            .then((html) => {
                if (!cancelled) setTranslatedHtml(html);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Report translation failed:', error);
                toast.error(t('ReportTranslationFailed'));
                setReportLanguage(SOURCE_REPORT_LANGUAGE);
            })
            .finally(() => {
                if (!cancelled) setIsTranslating(false);
            });

        return () => {
            cancelled = true;
        };
    }, [reportHtml, reportLanguage, isSourceLanguage, token, t]);

    const withErrorToast = useCallback(
        async (work) => {
            try {
                return await work();
            } catch (error) {
                console.error('Report translation failed:', error);
                toast.error(t('ReportTranslationFailed'));
                throw error;
            }
        },
        [t]
    );

    const translateHtml = useCallback(
        (html) => withErrorToast(() => translateReportHtml(html, reportLanguage, token)),
        [withErrorToast, reportLanguage, token]
    );

    const translateWb = useCallback(
        (wb) => withErrorToast(() => translateWorkbook(wb, reportLanguage, token)),
        [withErrorToast, reportLanguage, token]
    );

    return {
        reportLanguage,
        setReportLanguage,
        isTranslating,
        displayHtml: isSourceLanguage ? reportHtml : translatedHtml || reportHtml,
        previewDir: isSourceLanguage ? undefined : getReportDirection(reportLanguage),
        translateHtml,
        translateWb,
    };
};
